import {
  ChangeDetectorRef,
  Input,
  OnInit,
  OnDestroy,
  Component,
  SimpleChanges,
  OnChanges,
  inject,
  output
} from '@angular/core';
import { switchMap, catchError } from 'rxjs/operators';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';

import { ScrollDirection } from '../../domain/direction';
import { HelpService } from './help.service';
import { Article, Topic, ArticleUrl } from './help.domain';

@Component({
  template: '',
  host: {
    '[style.opacity]': 'styleOpacity',
    '[style.transitionDuration.ms]': 'styleTransitionDuration',
    '[style.transform]': 'styleTransform'
  }
})
export abstract class HelpBaseComponent implements OnInit, OnDestroy, OnChanges {
  readonly closeEvent = output<void>({ alias: 'close' });
  readonly articleIdChange = output<string>();

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get articleId(): string {
    return this.articleIdValue;
  }

  set articleId(value: string) {
    if (value !== this.articleIdValue) {
      this.articleIdValue = value;
      this.articleIdChange.emit(value);
    }
  }

  styleOpacity: string = '0';

  styleTransitionDuration: number = 150;

  styleTransform: string = 'translate3d(100%, 0, 0)';

  loading: boolean = false;
  topics: Array<Topic> = [];
  articles: Array<Article> = [];
  helpSubscription: Subscription;
  helpContentSubscription: Subscription;
  currentTopic: Topic;
  firstLoad: boolean = true;

  protected readonly destroy: Subject<void> = new Subject<void>();
  private readonly loadArticleContent: Subject<string> = new Subject<string>();
  private articleIdValue: string;

  protected readonly helpService = inject(HelpService);
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.helpContentSubscription = this.loadArticleContent
      .pipe(
        switchMap((articleId: string): Observable<Array<Article>> => {
          this.loading = true;
          return this.helpService.getHelpContent(this.currentTopic, articleId, this.articles)
            .pipe(
              catchError((): Observable<never> => EMPTY)
            );
        })
      )
      .subscribe({
        next: (response: Array<Article>): void => {
          this.helpService.mergeArticles(this.articles, response);
          this.loading = false;
          this.styleOpacity = '1';
          this.styleTransform = 'none';
          this.setScrollPostion()
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.loading = false;
        }
      });
    this.helpSubscription = this.helpService.getTopics()
      .subscribe((response: Array<Topic>): void => {
        this.topics = response;
        this.loadArticleById(this.articleId);
      });
  }

  ngOnDestroy(): void {
    if (this.helpSubscription !== undefined) {
      this.helpSubscription.unsubscribe();
    }
    if (this.helpContentSubscription !== undefined) {
      this.helpContentSubscription.unsubscribe();
    }
    this.destroy.next();
    this.destroy.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.articleId !== undefined &&
      changes.articleId.currentValue !== undefined &&
      !changes.articleId.firstChange) {
      this.articles = [];
      this.firstLoad = true;
      this.loadArticleById(this.articleId);

    }
  }

  loadTopic(topic: Topic): false {
    this.currentTopic = topic;
    this.articles = [];
    if (this.currentTopic.items.length > 0) {
      this.loadArticle(this.currentTopic.items[0].id);
    }
    return false;
  }

  loadMore(direction: ScrollDirection): void {
    if (!this.loading) {
      if (direction === ScrollDirection.DOWN) {
        this.loadNext();
      } else {
        this.loadPrevious();
      }
    }
  }

  close(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.closeEvent.emit();
  }

  trackByTopicId(_index: number, item: Topic): string {
    return item.id;
  }

  trackByArticleId(_index: number, item: Article): string {
    return item.id;
  }

  hasReachedTopicBottom(): boolean {
    if (this.currentTopic !== undefined &&
      this.currentTopic.items.length > 0 &&
      this.articles.length > 0) {
      const lastTopicItem = this.currentTopic.items[this.currentTopic.items.length - 1];
      const lastArticle = this.articles[this.articles.length - 1];
      return lastTopicItem.id === lastArticle.id;
    }
    return false;
  }

  hasReachedTopicTop(): boolean {
    if (this.currentTopic !== undefined &&
      this.currentTopic.items.length > 0 &&
      this.articles.length > 0) {
      const firstTopicItem = this.currentTopic.items[0];
      const firstArticle = this.articles[0];
      return firstTopicItem.id === firstArticle.id;
    }
    return false;
  }

  hasMoreThanOneLink(): boolean {
    return this.currentTopic !== undefined &&
      this.currentTopic.items.length > 1 &&
      this.articles.length > 1;
  }

  articleSelected(articleId: string): void {
    this.firstLoad = true;
    this.loadArticleById(articleId);
  }

  setScrollPostion(): void {
    if (this.firstLoad) {
      // Promise.resolve didn't work for some cases
      setTimeout(() => this.helpService.scrollToArticle(this.articleId));
      this.firstLoad = false;
    }
  }

  private loadArticleById(articleId: string): void {
    this.currentTopic = this.findTopic(articleId);
    if (this.currentTopic !== undefined) {
      this.loadArticle(articleId);
    } else {
      this.loadFirstAvailableArticle();
    }
  }

  private loadFirstAvailableArticle(): void {
    const firstTopic = this.topics.find((topic: Topic): boolean => topic.items !== undefined && topic.items.length > 0);
    if (firstTopic !== undefined) {
      this.currentTopic = firstTopic;
      this.loadArticle(firstTopic.items[0].id);
    }
  }

  private loadArticle(articleId: string): void {
    this.articleId = articleId;
    this.loadArticleContent.next(articleId);
  }

  private findTopic(articleId: string): Topic {
    return this.topics.find((topic: Topic): boolean => topic.items.some((item: ArticleUrl): boolean => item.id === articleId));
  }

  private loadNext(): void {
    const lastArticle = this.articles[this.articles.length - 1];
    const nextArticle = this.currentTopic.items[lastArticle.position + 1];
    if (nextArticle !== undefined) {
      this.loadArticle(nextArticle.id);
    }
  }

  private loadPrevious(): void {
    const firstArticle = this.articles[0];
    if (firstArticle !== undefined) {
      const previousArticle = this.currentTopic.items[firstArticle.position - 1];
      if (previousArticle !== undefined) {
        this.loadArticle(previousArticle.id);
      }
    }
  }
}
